import React, { useState, useEffect, useRef } from "react";
import { usePropertyStore } from "@/store/usePropertyStore";
import type { PropertyDefinition } from "@/types/property";

interface EditPropertyDialogProps {
  isOpen: boolean;
  propertyId: string;
  onClose: () => void;
}

/**
 * Edit Property Dialog
 *
 * Displays comprehensive property information in read-only format
 * Shows P_ID, assigned beams, material, section type, designation, and all properties
 *
 * Follows Theme_Format_readme.md standards:
 * - Draggable blue header
 * - Centered on open
 * - Modal with backdrop
 */
export const EditPropertyDialog: React.FC<EditPropertyDialogProps> = ({
  isOpen,
  propertyId,
  onClose,
}) => {
  const { getProperty } = usePropertyStore();
  const [property, setProperty] = useState<PropertyDefinition | null>(null);

  // Draggable state
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && propertyId) {
      const prop = getProperty(propertyId);
      setProperty(prop || null);
    }
  }, [isOpen, propertyId, getProperty]);

  // Center dialog on open
  useEffect(() => {
    if (isOpen && dialogRef.current) {
      const dialogWidth = 600;
      const dialogHeight = dialogRef.current.offsetHeight || 600;
      setPosition({
        x: Math.max(0, (window.innerWidth - dialogWidth) / 2),
        y: Math.max(0, (window.innerHeight - dialogHeight) / 2),
      });
    }
  }, [isOpen]);

  // Dragging handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest(".close-button")) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    document.body.style.userSelect = "none";
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const dialogHeight = dialogRef.current?.offsetHeight || 600;
      const newX = Math.max(
        0,
        Math.min(e.clientX - dragStart.x, window.innerWidth - 600)
      );
      const newY = Math.max(
        0,
        Math.min(e.clientY - dragStart.y, window.innerHeight - dialogHeight)
      );
      setPosition({ x: newX, y: newY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.body.style.userSelect = "";
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragStart]);

  if (!isOpen || !property) return null;

  // Format beam IDs display
  const beamIdsDisplay =
    property.beamIds?.length > 0
      ? property.beamIds.join(", ")
      : "No beams assigned";

  // Get section properties for display
  const sectionProps = property.sectionProperties || {};
  const hasProperties = Object.keys(sectionProps).length > 0;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-30 z-50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[600px] max-h-[80vh] overflow-hidden z-50"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
      >
        {/* Draggable Header */}
        <div
          className="bg-blue-600 text-white px-4 py-3 rounded-t-lg cursor-grab active:cursor-grabbing select-none flex justify-between items-center"
          onMouseDown={handleMouseDown}
        >
          <h2 className="text-xl font-semibold">
            Property Details: {property.P_ID}
          </h2>
          <button
            onClick={onClose}
            className="close-button text-white hover:text-gray-200 p-1 rounded transition-colors"
            title="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-140px)] p-6">
          {/* Basic Information Section */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 border-b pb-2">
              Basic Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Property ID
                </label>
                <div className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
                  {property.P_ID}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Material
                </label>
                <div className="mt-1 text-base text-gray-900 dark:text-gray-100">
                  {property.material || "None"}
                </div>
              </div>
              <div className="col-span-2">
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Assigned Beams ({property.beamIds?.length || 0})
                </label>
                <div className="mt-1 p-3 bg-gray-50 dark:bg-gray-700 rounded text-sm text-gray-900 dark:text-gray-100 break-words">
                  {beamIdsDisplay}
                </div>
              </div>
            </div>
          </div>

          {/* Section Information */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 border-b pb-2">
              Section Information
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Section Type
                </label>
                <div className="mt-1 text-base text-gray-900 dark:text-gray-100">
                  {property.sectionType || "Not specified"}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Designation
                </label>
                <div className="mt-1 text-base font-mono text-gray-900 dark:text-gray-100">
                  {sectionProps.designation || "Not specified"}
                </div>
              </div>
            </div>
          </div>

          {/* Detailed Properties */}
          {hasProperties && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 border-b pb-2">
                Section Properties
              </h3>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                  {Object.entries(sectionProps)
                    .filter(([key]) => key !== "designation") // Skip designation as it's shown above
                    .map(([key, value]) => (
                      <div
                        key={key}
                        className="flex justify-between items-center"
                      >
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                          {key}:
                        </span>
                        <span className="text-sm font-mono text-gray-900 dark:text-gray-100 ml-2">
                          {typeof value === "number"
                            ? value.toFixed(3)
                            : String(value)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          {/* Metadata */}
          {/* <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-2 gap-4 text-xs text-gray-500 dark:text-gray-400">
              <div>
                <span className="font-medium">Created:</span>{" "}
                {property.createdAt
                  ? new Date(property.createdAt).toLocaleString()
                  : "N/A"}
              </div>
              <div>
                <span className="font-medium">Updated:</span>{" "}
                {property.updatedAt
                  ? new Date(property.updatedAt).toLocaleString()
                  : "N/A"}
              </div>
            </div>
          </div> */}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-600 px-6 py-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};
